<template>
  <div class="changelog-page">
    <div class="changelog-header">
      <button @click="goBack" class="btn-back">
        ← Retour
      </button>
      <h1>Historique des versions</h1>
      <p class="subtitle">Découvrez les nouveautés et améliorations de la plateforme</p>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Chargement...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
    </div>

    <div v-else class="changelog-content">
      <div
        v-for="version in changelog.versions"
        :key="version.version"
        class="version-card"
      >
        <div class="version-header">
          <div class="version-title">
            <span class="version-number">v{{ version.version }}</span>
            <span :class="['version-type', `version-type-${version.type.toLowerCase()}`]">
              {{ version.type }}
            </span>
          </div>
          <span class="version-date">{{ formatDate(version.date) }}</span>
        </div>

        <h2 class="version-name">{{ version.title }}</h2>
        <p class="version-description">{{ version.description }}</p>

        <div v-if="version.features.length > 0" class="version-section">
          <h3>Nouvelles fonctionnalités</h3>
          <div
            v-for="(category, idx) in version.features"
            :key="idx"
            class="feature-category"
          >
            <h4>{{ category.category }}</h4>
            <ul>
              <li v-for="(item, itemIdx) in category.items" :key="itemIdx">
                {{ item }}
              </li>
            </ul>
          </div>
        </div>

        <div v-if="version.improvements && version.improvements.length > 0" class="version-section">
          <h3>Améliorations</h3>
          <ul>
            <li v-for="(item, idx) in version.improvements" :key="idx">
              {{ item }}
            </li>
          </ul>
        </div>

        <div v-if="version.fixes && version.fixes.length > 0" class="version-section">
          <h3>Corrections</h3>
          <ul>
            <li v-for="(item, idx) in version.fixes" :key="idx">
              {{ item }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="!loading && !error" class="changelog-footer">
      <p>Système de versionnement : <strong>GoRoCo</strong> (Génération.Révision.Correction)</p>
      <p class="last-update">Dernière mise à jour : {{ formatDate(changelog.metadata.lastUpdate) }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import changelogData from '../assets/CHANGELOG.json';

interface FeatureCategory {
  category: string;
  items: string[];
}

interface Version {
  version: string;
  date: string;
  type: string;
  title: string;
  description: string;
  features: FeatureCategory[];
  improvements: string[];
  fixes: string[];
}

interface Changelog {
  versions: Version[];
  metadata: {
    project: string;
    versioningSystem: string;
    lastUpdate: string;
  };
}

const router = useRouter();
const loading = ref(true);
const error = ref<string | null>(null);
const changelog = ref<Changelog>(changelogData as Changelog);

const goBack = () => {
  router.back();
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

onMounted(() => {
  try {
    loading.value = false;
  } catch (err: any) {
    error.value = 'Erreur lors du chargement de l\'historique';
    loading.value = false;
  }
});
</script>

<style scoped>
.changelog-page {
  min-height: 100vh;
  background-color: #1a1a1a;
  color: #e0e0e0;
  padding: 2rem;
  overflow-y: auto;
}

.changelog-header {
  max-width: 900px;
  margin: 0 auto 3rem;
  padding: 2rem;
  background: linear-gradient(135deg, rgba(74, 158, 255, 0.1) 0%, rgba(74, 158, 255, 0.05) 100%);
  border-radius: 12px;
  border: 1px solid rgba(74, 158, 255, 0.2);
}

.btn-back {
  padding: 0.5rem 1rem;
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: #e0e0e0;
  cursor: pointer;
  font-size: 0.9rem;
  margin-bottom: 2rem;
  transition: all 0.2s;
}

.btn-back:hover {
  background-color: #444;
  border-color: #555;
}

.changelog-header h1 {
  font-size: 3rem;
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-weight: 700;
  letter-spacing: -0.5px;
}

.subtitle {
  color: #999;
  font-size: 1.2rem;
  font-weight: 300;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 3rem;
  color: #999;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #444;
  border-top-color: #4a9eff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-state {
  color: #dc3545;
}

.changelog-content {
  max-width: 900px;
  margin: 0 auto;
}

.version-card {
  background-color: #2a2a2a;
  border: 1px solid #444;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
}

.version-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.version-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.version-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: #4a9eff;
}

.version-type {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.version-type-génération {
  background-color: rgba(74, 158, 255, 0.2);
  color: #4a9eff;
}

.version-type-révision {
  background-color: rgba(40, 167, 69, 0.2);
  color: #28a745;
}

.version-type-correction {
  background-color: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.version-date {
  color: #666;
  font-size: 0.9rem;
}

.version-name {
  font-size: 1.3rem;
  margin-bottom: 0.75rem;
  color: #e0e0e0;
}

.version-description {
  color: #999;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.version-section {
  margin-top: 1.5rem;
}

.version-section h3 {
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
  color: #e0e0e0;
}

.feature-category {
  margin-bottom: 1rem;
}

.feature-category h4 {
  font-size: 1rem;
  color: #4a9eff;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.version-section ul {
  list-style: none;
  padding-left: 0;
}

.version-section li {
  padding: 0.4rem 0;
  padding-left: 1.5rem;
  position: relative;
  color: #ccc;
  line-height: 1.5;
}

.version-section li::before {
  content: '•';
  position: absolute;
  left: 0.5rem;
  color: #4a9eff;
  font-weight: bold;
}

.changelog-footer {
  max-width: 900px;
  margin: 3rem auto 0;
  padding-top: 2rem;
  border-top: 1px solid #444;
  text-align: center;
  color: #666;
  font-size: 0.9rem;
}

.changelog-footer strong {
  color: #4a9eff;
}

.last-update {
  margin-top: 0.5rem;
  font-size: 0.85rem;
}
</style>
